import axios from './axios';
import { MEMBER_PROFILE_SERVICE } from '../common/constant/app.properties';
const { REACT_APP_SERVICE_URL } = window.weeksWebCloudConfig;

/**
 * Decode and sanitize param values — strips encoded artifacts (%5E, %22, &amp;%3B)
 * that leak in from browser extensions/proxies/antivirus for ~2% of users.
 */
const decodeParam = (val) => {
  if (val == null) return val;
  let str = String(val);
  try { str = decodeURIComponent(str); } catch (e) { /* already decoded */ }
  return str.replace(/[\^"]/g, '').replace(/&amp;/gi, '&').trim();
};

/**
 * Custom params serializer to guarantee clean query strings,
 * bypassing any external interference with axios default serialization.
 */
const cleanParamsSerializer = (params) => {
  return Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(decodeParam(value))}`)
    .join('&');
};

/**
 * Log errors directly to Splunk via the /utilities/error/logerror endpoint.
 * This is a fire-and-forget call — failures are silently ignored to avoid loops.
 */
export const logErrorToSplunk = (error, url, method, businessFunc = 'getDataFromAPI') => {
  try {
    const customerDetails = localStorage.getItem('_customerDetails');
    const { locale = '', memberId = '', memberTierType = '' } = customerDetails ? JSON.parse(customerDetails) : {};

    // Get the actual full URL from the axios error config (includes query params as sent).
    // Serialize with the SAME cleanParamsSerializer the real request uses so the logged URL
    // mirrors what was actually sent — otherwise URLSearchParams stringifies missing values
    // (e.g. an undefined `locale`) into the literal text "locale=undefined".
    const actualUrl = error?.config?.url || url;
    const actualParams = error?.config?.params;
    const actualQueryString = actualParams ? cleanParamsSerializer(actualParams) : '';
    const fullUrl = actualQueryString ? `${actualUrl}?${actualQueryString}` : actualUrl;

    // Capture the request payload (body) sent with the failed call.
    // axios stores it on error.config.data — usually a JSON string, but can be a plain object/FormData.
    let requestPayload = '';
    try {
      const rawData = error?.config?.data;
      if (rawData != null) {
        if (typeof rawData === 'string') {
          requestPayload = rawData;
        } else if (rawData instanceof FormData) {
          const formObj = {};
          rawData.forEach((v, k) => { formObj[k] = v; });
          requestPayload = JSON.stringify(formObj);
        } else {
          requestPayload = JSON.stringify(rawData);
        }
      }
    } catch (e) {
      requestPayload = '';
    }

    // Classify the failure with an ACCURATE, client-scoped label.
    // axios reports the literal string "Network Error" for ANY failure where no HTTP response was
    // received (request interrupted, user navigated away, CORS, connection drop) — it does NOT mean
    // the user's network is down, and it is NOT a backend error. Relabel it so the backend/ops team
    // is not misled into chasing a server/network outage.
    const httpStatus = error?.response?.status;            // present ONLY when the server responded
    const axiosCode = error?.code;                         // real axios code when present (e.g. ECONNABORTED on timeout)
    const isNoResponse = httpStatus == null && (error?.message === 'Network Error' || axiosCode === 'ERR_NETWORK');

    let errorLabel;
    let errorCode;
    if (httpStatus != null) {
      errorLabel = error?.message || `Request failed with status code ${httpStatus}`;
      errorCode = String(httpStatus);                      // real backend status: 400 / 500 / 502 ...
    } else if (isNoResponse) {
      errorLabel = 'No Response Received (client-side)';   // <- replaces the misleading "Network Error"
      errorCode = 'CLIENT_NO_RESPONSE';
    } else if (axiosCode) {
      errorLabel = error?.message || axiosCode;            // e.g. timeout -> ECONNABORTED
      errorCode = axiosCode;
    } else {
      errorLabel = error?.message || 'Unknown';
      errorCode = 'UNKNOWN';
    }

    const sampleText = `[RCI-WEEKS-WEB] API call failed | ${method} ${fullUrl} | Error: ${errorLabel} | Code: ${errorCode} | Member: ${memberId} | Page: ${window.location.pathname}`;

    // const errorMessage = [
    //   `[RCI-WEEKS-WEB]`,
    //   `Action: ${businessFunc}`,
    //   `Method: ${method}`,
    //   `URL: ${fullUrl}`,
    //   `Error: ${error?.message || 'Unknown'}`,
    //   `Code: ${errorCode}`,
    //   `Member: ${memberId}`,
    //   `Tier: ${memberTierType}`,
    //   `Locale: ${locale}`,
    //   `Page: ${window.location.pathname}`,
    //   `Host: ${window.location.host}`,
    //   `Browser: ${window.navigator.userAgent}`,
    //   requestPayload ? `Payload: ${requestPayload.substring(0, 1500)}` : '',
    // ].filter(Boolean).join(' | ');

    const requestBody = {
      application: 'WOW',
      browser: window.navigator.userAgent,
      businessFunction: businessFunc,
      channel: 'WEB',
      customerId: memberId,
      errorCode: errorCode,
      errorMessage: sampleText,
      errorStacktrace: error?.stack || '',
      errorType: 'System',
      lineOfBusiness: memberTierType,
      locale: locale,
      pageName: window.location.pathname,
      productType: '',
      // NOTE: the backend ExceptionLoggerService does NOT echo the `requestPayload` field into the
      // Splunk log line — it only surfaces EM and the <input>(=request)/<output>(=response) tags.
      // So the payload MUST be embedded in `request` (→ <input>) to actually appear in Splunk.
      request: requestPayload
        ? `${method} ${fullUrl} | Payload: ${requestPayload.substring(0, 1500)}`
        : `${method} ${fullUrl}`,
      requestPayload: requestPayload,
      requestId: error?.response?.headers?.traceid || '',
      response: error?.response?.data ? JSON.stringify(error.response.data) : '',
      serverName: window.location.host,
      sessionId: '',
      tealeafId: '',
      userIP: '',
    };

    // Fire-and-forget — do not await, do not catch (prevents infinite loop)
    axios({
      url: `${REACT_APP_SERVICE_URL}/utilities/error/logerror?ConsumerChannel=Web&MemberType=WEEKS&OperatorId=WEB00USER`,
      method: 'POST',
      headers: { 'content-type': 'text/plain' },
      data: requestBody,
      withCredentials: true,
    }).catch(() => {}); // silently ignore logging failures
  } catch (e) {
    // Never let error logging break the app
  }
};

// Please this method takes URL , Method As mentadory params, Access token and Body optional
export const getDataFromAPI = async (url, method, customerID, body, access_token, language, locale) => {
  // const customerID = useSelector(state => state.CommonReducer.customerId)
  //debugger;

  const headers = {
    'Content-Type': 'text/plain'
  };
  let axiosPostCallRes = ""
  let queryParam = {
    consumerChannelQ: "Web",
    memberTypeQ: "WEEKS",
    operatorIdQ: "WEB00USER",
    /* Authorization: `Bearer ${access_token}`, */
    customerIdQ: decodeParam(customerID),

  }
  if (language || locale) {
    queryParam = {
      consumerChannelQ: "Web",
      memberTypeQ: "WEEKS",
      operatorIdQ: "WEB00USER",
      /* Authorization: `Bearer ${access_token}`, */
      languageQ: decodeParam(language).toUpperCase(),
      localeQ: decodeParam(locale),
      customerIdQ: decodeParam(customerID),

    }
  }
  if (method === 'POST') {
    try {
      //axiosPostCallRes = await axios.post(url,body,headers);
      axiosPostCallRes = await axios({
        url: url, method: method,
        params: queryParam, paramsSerializer: cleanParamsSerializer,
        headers: headers, data: body, withCredentials: true
      })
      return axiosPostCallRes;
    } catch (error) {
      //refreshToken(customerID,locale)
      console.error('RCI weeks error:', 'Error in API call common function url:', `${url}?${new URLSearchParams(queryParam).toString()}`, error);
      console.log('RCI weeks error:', 'Error in API call common function url:', `${url}?${new URLSearchParams(queryParam).toString()}`, error);
      // logErrorToSplunk(error, url, method, 'getDataFromAPI POST');
      return error;
    }
  } else if (method === 'PUT') {
    try {
      //axiosPostCallRes = await axios.PUT(url,body,headers);
      axiosPostCallRes = await axios({
        url: url, method: method,
        params: queryParam, paramsSerializer: cleanParamsSerializer,
        data: body, withCredentials: true
      })
      return axiosPostCallRes;
    } catch (error) {
      console.error('RCI weeks error:', 'Error in API call common function url:', `${url}?${new URLSearchParams(queryParam).toString()}`, error);
      console.log('RCI weeks error:', 'Error in API call common function url:', `${url}?${new URLSearchParams(queryParam).toString()}`, error);
      // logErrorToSplunk(error, url, method, 'getDataFromAPI PUT');
      return error;
    }
  } else if (method === 'GET') {
    try {
      //axiosPostCallRes= await axios.get(url,headers)
      axiosPostCallRes = await axios({
        url: url, method: method,
        params: queryParam, paramsSerializer: cleanParamsSerializer,
        data: null, withCredentials: true
      })
      return axiosPostCallRes

    } catch (error) {
      // logErrorToSplunk(error, url, method, 'getDataFromAPI GET');
      return error;
    }

  }
  /*  try {
       //axiosPostCallRes= await axios.get(url,headers)
       axiosPostCallRes = await axios({url:url,method: method,
           params:queryParam, data:null, withCredentials: true
           
   })
            return axiosPostCallRes;
 
   } catch (error) {
       return error;
   }  */
}


export const retryServiceCall = async ($customerID, $locale) => {
  let refreshTokenRes = await axios({
    url: `${REACT_APP_SERVICE_URL}/validation/jwt`,
    method: 'post',
    withCredentials: true,
    /* headers: { 
    Authorization: `Bearer ${token}`,
    byPassCookie:true
    } */
    params: {
      customerId: decodeParam($customerID),
      locale: decodeParam($locale)
    },
    paramsSerializer: cleanParamsSerializer
  })
  return refreshTokenRes.data;
}
export const getServiceCallPromise = ({
  resource,
  method,
  loginHeader,
  data,
  customHeaders = {},
  withCredentials = true,
  responseType = 'json',
  replaceUrlWithResource,
  contentType,
}) => {
  const { REACT_APP_SERVICE_URL } = window.weeksWebCloudConfig;

  const safeResource = decodeParam(resource);
  const COMMON_QUERY_PARAM = 'ConsumerChannel=Web&MemberType=WEEKS&OperatorId=WEB00USER';
  let customResource = safeResource && safeResource.includes('?') ? `${safeResource}&${COMMON_QUERY_PARAM}` : `${safeResource}?${COMMON_QUERY_PARAM}`;
  if (customResource.includes('aem')) {
    customResource = safeResource && safeResource.includes('?') ? `${safeResource}&consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER` : `${safeResource}?consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER`;
  } else if (safeResource.includes('weeks-offers-details') || safeResource.includes('mocky')) {
    customResource = safeResource;
  } else if (customResource.includes('pagerules')) {
    customResource = safeResource && safeResource.includes('?') ? `${safeResource}&consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER` : `${safeResource}?consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER`;
  } else if (customResource.includes('post-wow')) {
    customResource = safeResource;
  }



  let headers = {};
  headers = {
    'content-type': 'text/plain',
  };

  if (loginHeader) {
    headers.cbBrand = loginHeader.cbBrand;
  }
  return axios({
    url: replaceUrlWithResource ? customResource : `${REACT_APP_SERVICE_URL}${customResource}`,
    responseType,
    method,
    data,
    headers,
    withCredentials,
  });
};
export const invokeService = async (args) => {
  try {
    const serviceResponse = await getServiceCallPromise(args);
    return serviceResponse;
  } catch (error) {
    return { error, data: args };
  }
};


// Use this method for AU service call
export const getDataFromService = async ({
  url,
  method,
  customerID,
  userLocale,
  requestBody,
  appType,
}) => {
  let serviceCallRes = '';
  let headers = {};
  headers = {
    'content-type': 'text/plain',
  };
  let queryParam = {
    locale: decodeParam(userLocale),
    consumerChannelQ: 'Web',
    memberTypeQ: 'WEEKS',
    operatorIdQ: 'WEB00USER',
    customerIdQ: decodeParam(customerID),
  };
  if (appType) {
    queryParam = { ...queryParam, appType };
  }
  serviceCallRes = await axios({
    url,
    method,
    headers,
    params: queryParam,
    paramsSerializer: cleanParamsSerializer,
    data: requestBody,
    withCredentials: true,
  }).catch(error => error);
  return serviceCallRes;
};

export const getDataFromServiceNew = async ({
  url,
  method,
  customerID,
  userLocale,
  requestBody,
  appType,
}) => {
  let serviceCallRes = '';
  let headers = {};
  headers = {
    'content-type': 'application/json',
    ConsumerChannel: 'Web',
    MemberType: 'WEEKS',
    OperatorId: 'WEB00USER',
  };
  let queryParam = {
    locale: decodeParam(userLocale),
    consumerChannelQ: 'Web',
    memberTypeQ: 'WEEKS',
    operatorIdQ: 'WEB00USER',
    customerIdQ: decodeParam(customerID),
  };
  if (appType) {
    queryParam = { ...queryParam, appType };
  }
  serviceCallRes = await axios({
    url,
    method,
    headers,
    params: queryParam,
    paramsSerializer: cleanParamsSerializer,
    data: requestBody,
    withCredentials: true,
  }).catch(error => error);
  return serviceCallRes;
};


export const getFavoriteResorts = ({ subscriberId  }) => async (dispatch) => {
  const memberUrl = `${REACT_APP_SERVICE_URL}${MEMBER_PROFILE_SERVICE}/${subscriberId }/preferences/favorites-resort`;

  const serviceResponse = await getDataFromService({ url: memberUrl, method: 'GET', customerID: subscriberId  });
  if (serviceResponse.status === 200) {
    // console.log(serviceResponse, 'serviceResponse');
    const listOfFavoriteResorts = serviceResponse.data.map(
      eachResortInfo => eachResortInfo?.resortID,
    );
    // dispatch(fetchFavoriteResortsSuccess(listOfFavoriteResorts));
    dispatch({ type: 'update_resort_Data', data: listOfFavoriteResorts });
  } else {
    dispatch({ type: 'fav_resort_failure', data: true });
    // dispatch(logErrorMessage(serviceResponse, 'Favorites Get Favorite Resorts'));
  }
};
